import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './marketing.module.css';

interface SectionProps {
  id: string;
  /** The id of the heading that names this section. Required: every section is a labelled landmark. */
  labelledBy: string;
  children: ReactNode;
  /** `dark` paints the solid midnight surface and flips the text and focus colours. */
  tone?: 'light' | 'dark';
  className?: string;
}

/**
 * A page section: standard block padding, a scroll margin that clears the
 * floating header, and an optional midnight tone. Sections separate by padding
 * alone; there are no full-width rules between them, so none is drawn here.
 *
 * A dark section also declares itself to the floating header via
 * `data-header-zone`, which is how the bar knows to go back to glass over it.
 * See `HomepageHeader` for the contract.
 */
export function Section({
  id,
  labelledBy,
  children,
  tone = 'light',
  className,
}: SectionProps): React.ReactElement {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={clsx(styles.section, tone === 'dark' && styles.dark, className)}
      data-header-zone={tone === 'dark' ? 'dark' : undefined}
    >
      {children}
    </section>
  );
}
